import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Zap, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TRIGGERS = [
  { value: "ticket.created", label: "Ticket created" },
  { value: "ticket.status_changed", label: "Status changed" },
  { value: "ticket.sla_breach", label: "SLA breach detected" },
  { value: "ticket.reminder", label: "Reminder sent" },
  { value: "ticket.assigned", label: "Ticket assigned" },
  { value: "ticket.commented", label: "New comment" },
];

const ACTIONS = [
  { value: "notify_manager", label: "Notify manager" },
  { value: "notify_dept_poc", label: "Notify dept POC" },
  { value: "escalate_priority", label: "Escalate priority" },
  { value: "assign_to_l2", label: "Reassign to L2 agent" },
  { value: "send_email", label: "Send transactional email" },
  { value: "set_status_pending_user", label: "Set status → pending_user" },
];

const BLANK = { name: "", trigger: "ticket.sla_breach", condition: "priority == 'critical'", action: "notify_manager", active: true };

export default function AutomationsPanel() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("automation.create");
  const canEdit = hasPerm("automation.edit");
  const canDelete = hasPerm("automation.delete");
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/automations").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (rule) => {
    const updated = { ...rule, active: !rule.active };
    await api.patch(`/automations/${rule.id}`, {
      name: updated.name, trigger: updated.trigger,
      condition: updated.condition, action: updated.action, active: updated.active,
    });
    setRules((rs) => rs.map((r) => (r.id === rule.id ? updated : r)));
    toast.success(updated.active ? "Rule activated" : "Rule paused");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/automations/${id}`);
    setRules((rs) => rs.filter((r) => r.id !== id));
    toast.success("Rule deleted");
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setSubmitting(true);
    try {
      await api.post("/automations", form);
      toast.success("Rule created");
      setOpen(false); setForm(BLANK);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="automations-panel">
      <div className="flex items-center justify-end">
        {canCreate && (
          <Button onClick={() => setOpen(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-automation-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New rule
          </Button>
        )}
      </div>

      {rules.map((r) => (
        <Card key={r.id} className="border-slate-200" data-testid={`automation-${r.id}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 grid place-items-center rounded-md ${r.active ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-400"}`}>
                <Zap className="w-4 h-4" />
              </div>
              <div>
                <CardTitle className="font-heading text-base font-semibold">{r.name}</CardTitle>
                <Badge
                  variant="outline"
                  className={`mt-1.5 text-[10px] ${r.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}
                >
                  {r.active ? "Active" : "Paused"}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canEdit && <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`automation-toggle-${r.id}`} />}
              {canDelete && (
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(r.id)} data-testid={`automation-delete-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Trigger</div>
              <div className="text-slate-700 font-mono text-xs">{r.trigger}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Condition</div>
              <div className="text-slate-700 font-mono text-xs">{r.condition}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Action</div>
              <div className="text-slate-700 font-mono text-xs">{r.action}</div>
            </div>
          </CardContent>
        </Card>
      ))}
      {rules.length === 0 && (
        <div className="text-center py-16 text-slate-400 text-sm">
          No automation rules yet.{canCreate && <> <button onClick={() => setOpen(true)} className="text-indigo-600 hover:underline">Create your first rule</button>.</>}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="automation-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">New automation rule</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Triggers fire when an event happens. Conditions are simple expressions evaluated against the ticket.
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Critical breach → notify manager"
                data-testid="automation-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Trigger</Label>
                <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                  <SelectTrigger data-testid="automation-trigger-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Action</Label>
                <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                  <SelectTrigger data-testid="automation-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Condition <span className="text-slate-400 font-normal">(simple expression)</span></Label>
              <Input
                value={form.condition}
                onChange={(e) => setForm({ ...form, condition: e.target.value })}
                placeholder="priority == 'critical' && department == 'Finance'"
                className="font-mono text-xs"
                data-testid="automation-condition-input"
              />
              <p className="text-[10px] text-slate-400">
                Tip: use ticket fields like <code className="font-mono">priority</code>, <code className="font-mono">status</code>, <code className="font-mono">department</code>, <code className="font-mono">channel</code>.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-xs text-slate-600">{form.active ? "Active on creation" : "Create paused"}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.name}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="automation-submit-btn"
            >
              {submitting ? "Saving..." : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
